/**
 * @file WriteoffDocHeader.tsx
 * @description Шапка документа «Списание»: номер, статус, плитки meta и
 * действия (phase19.24 этап 1, шаг 1; phase19.31 зона «Склад» — шапка в
 * верхней панели, `WmsDocumentPageHeader`).
 *
 * У списания два состояния: черновик проводим, по проведённому печатаем акт
 * (на черновик сервер отвечает 409 `writeoff_not_committed`). Одна главная
 * кнопка, «⋯» — звук скана; причина блокировки — подписью (на телефоне —
 * над кнопкой общей нижней полосы).
 */

import { Check } from "lucide-react";

import { documentCommitHint } from "./document-texts";
import { useScanSoundAction } from "./useScanSoundAction";
import { WmsDocumentPageHeader } from "./WmsDocumentPageHeader";
import { WriteoffActPrintButton } from "./WriteoffActPrintButton";
import { buildDocumentMeta } from "./wms-document-meta";
import { Button } from "../ui/button";
import { StatusPill } from "../ui/StatusPill";
import { WRITEOFF_REASON_LABEL, type WriteoffDto } from "../../lib/api-wms";
import { WMS_DOCUMENT_STATUS_META } from "../../lib/shop/status-meta";

interface Props {
  writeoff: WriteoffDto;
  /** Реестр списаний — «назад». */
  backTo: string;
  committing: boolean;
  onCommit: () => void;
}

export function WriteoffDocHeader({ writeoff, backTo, committing, onCommit }: Props) {
  const isDraft = writeoff.status === "DRAFT";
  const items = writeoff.items ?? [];
  const hint = documentCommitHint("writeoff", items.length);
  const scanSound = useScanSoundAction();

  return (
    <WmsDocumentPageHeader
      number={writeoff.writeoffNumber}
      status={<StatusPill meta={WMS_DOCUMENT_STATUS_META[writeoff.status]} />}
      subtitle={`${writeoff.warehouse?.name ?? "—"} · ${WRITEOFF_REASON_LABEL[writeoff.reason]}`}
      back={{ to: backTo, label: "К списаниям" }}
      meta={buildDocumentMeta({
        authorLabel: "Создал",
        author: writeoff.createdBy?.displayName,
        createdAt: writeoff.createdAt,
        committedAt: writeoff.committedAt,
        items,
      })}
      action={
        isDraft ? (
          <Button variant="brand" onClick={onCommit} disabled={!!hint || committing} loading={committing}>
            <Check className="size-4" />
            Провести списание
          </Button>
        ) : (
          <WriteoffActPrintButton writeoffId={writeoff.id} number={writeoff.writeoffNumber} />
        )
      }
      menu={[scanSound]}
      menuLabel="Действия со списанием"
      hint={isDraft ? hint : null}
    />
  );
}
